import {
    AiImageAttachment,
    AiInferenceMessage,
    AiInferenceResponse,
    AiInferenceTool,
    LogLevel,
    BillingPeriod as CommonBillingPeriod,
    BillingReport as CommonBillingReport,
    Product,
    LicenseEntitlements,
    ListResult,
    AuditFieldChange,
    AuditFilter as CommonAuditFilter,
    AuditLogOperationResult,
    ApplianceCodecCapacity,
    ApplianceVariant,
} from 'common/api/v1/types'
export enum AuditOperation {
    create = 'create',
    delete = 'delete',
    update = 'update',
    read = 'read',
    login = 'login',
    logout = 'logout',
}

export interface AuditRefData {
    [prop: string]: string | number | boolean | undefined
}
/** Who made an audited change: Net Insight staff, a Console user, or the NetSuite sync. */
export enum AuditSource {
    internal = 'internal',
    external = 'external',
    netsuiteSync = 'netsuiteSync',
}

export interface AuditLog {
    id: string
    operation: AuditOperation
    entity: string
    entityId?: string
    entityName: string
    // pathname: string
    // query: string
    userId: string
    username: string
    actorType: AccountType
    source: AuditSource
    customerId?: string
    customerName?: string
    clientIp?: string // Only visible for super user
    // request?: string
    // response?: string
    error?: string
    executionTimeMs?: number
    createdAt: Date
    updatedAt?: Date
    changes?: AuditFieldChange[]
}

export interface BillingAuditFilter extends CommonAuditFilter {
    /** Include staff actions. Defaults to true. */
    internal?: boolean
    /** Include Console actions. Defaults to true. */
    external?: boolean
    /** Include changes the NetSuite sync made or refused. Defaults to true. */
    netsuiteSync?: boolean
    customerName?: string
}

export interface ConsoleAuditLog {
    id: string
    operation: AuditOperation
    entity: string
    entityId?: string
    entityName: string
    username: string
    result: AuditLogOperationResult
    createdAt: Date
    changes?: AuditFieldChange[]
}

export enum Role {
    externalBasic = 'external basic', // External customer user without allocation or user-management rights
    external = 'external', // External customer administrator: manages organization users, licenses, and RTUs
    invoiceOnly = 'invoice only',
    licenseAdmin = 'license admin', // Cannot create customers
    admin = 'admin', // Can create customers
    super = 'super',
}

export enum AccountType {
    internal = 'internal',
    external = 'external',
}

export interface GlobalSettings {
    logLevel: LogLevel
    mfaEnforced: boolean
    /** Omitted when a caller only changes the log level; the stored AI configuration is preserved. */
    ai?: AiGlobalSettings
    /** Omitted by callers that do not change it; the stored NetSuite configuration is preserved. */
    netsuite?: NetSuiteGlobalSettings
}

/** Operator-controlled switch for the NetSuite import. The cadence stays a deployment setting. */
export interface NetSuiteGlobalSettings {
    syncEnabled: boolean
}

/** Inference providers billing-server can route an assistant job to. */
export enum AiProvider {
    bedrockMantle = 'bedrock_mantle',
    anthropic = 'anthropic',
}

export const AI_PROVIDERS = [AiProvider.bedrockMantle, AiProvider.anthropic] as const

export const isAiProvider = (value: unknown): value is AiProvider =>
    (AI_PROVIDERS as readonly string[]).includes(value as string)

export interface AiGlobalSettings {
    trialCreditMicroUsd: number
    monthlyBudgetMicroUsd: number
    maxJobCostMicroUsd: number
    maxLedgerEntriesPerJob: number
    /** Share of new assistant jobs routed to the direct Anthropic API; the remainder go to Bedrock Mantle. */
    anthropicTrafficPercent: number
    mantleRegion: string
    model: string
    maxTokens: number
    expertModel: string
    expertMaxTokens: number
    classifierModel: string
    classifierMaxTokens: number
    datasetAutoUpdate: boolean
    datasetVersion?: string
}

/** AI configuration shipped by a fresh installation and certified by the evaluation gate. */
export const DEFAULT_AI_GLOBAL_SETTINGS = {
    monthlyBudgetMicroUsd: 200_000_000,
    maxJobCostMicroUsd: 40_000_000,
    maxLedgerEntriesPerJob: 96,
    anthropicTrafficPercent: 50,
    mantleRegion: 'us-east-1',
    model: 'anthropic.claude-opus-5-5',
    maxTokens: 16_384,
    expertModel: 'anthropic.claude-opus-5-5',
    expertMaxTokens: 16_384,
    classifierModel: 'anthropic.claude-haiku-4-5',
    classifierMaxTokens: 256,
} as const satisfies Omit<AiGlobalSettings, 'trialCreditMicroUsd' | 'datasetAutoUpdate' | 'datasetVersion'>

export interface AiDatasetVersion {
    version: string
    commit: string
    createdAt: Date
}

export interface AiDatasetStatus {
    autoOffset: number
    effectiveVersion: string
    servingVersion: string
    servingIsFallback: boolean
    servingAgeSeconds: number
    availableVersions: AiDatasetVersion[]
}

export type AiModelConfiguration = Pick<AiGlobalSettings, 'mantleRegion' | 'model' | 'maxTokens'>

export interface AiModelInfo {
    id: string
    /** Providers that expose this canonical model, so operators can see what a model change costs them. */
    providers: AiProvider[]
}

export enum AiCustomerStatus {
    trial = 'trial',
    active = 'active',
    off = 'off',
}

export enum AiBillingResource {
    aiAssistant = 'ai_assistant',
}

export enum AiInferencePurpose {
    main = 'main',
    title = 'title',
    scopeClassifier = 'scope_classifier',
    expert = 'expert',
    expertReviewer = 'expert_reviewer',
}

export enum AiPolicyOutcome {
    notApplicable = 'not_applicable',
    allowed = 'allowed',
    blocked = 'blocked',
    revisionRequested = 'revision_requested',
    failedClosed = 'failed_closed',
}

export interface AiModelPrice {
    id: string
    provider: AiProvider
    model: string
    effectiveAt: Date
    inputMicroUsdPerMillionTokens: number
    outputMicroUsdPerMillionTokens: number
    cacheWriteMicroUsdPerMillionTokens: number
    cacheReadMicroUsdPerMillionTokens: number
    createdAt: Date
}

export type AiModelPriceInit = Omit<AiModelPrice, 'id' | 'createdAt'>

export interface AiInferenceLedgerEntry {
    id: string
    resource: AiBillingResource.aiAssistant
    customerId: string
    installationId: string
    edgeUserId: string
    edgeUsername: string
    provider: AiProvider
    model: string
    region: string
    priceId: string
    inputTokens: number
    outputTokens: number
    cacheWriteTokens: number
    cacheReadTokens: number
    costMicroUsd: number
    outcome: 'completed' | 'provider_error' | 'timeout' | 'usage_unavailable'
    purpose: AiInferencePurpose
    policyOutcome: AiPolicyOutcome
    idempotencyKey: string
    startedAt: Date
    completedAt?: Date
}

export interface AiUsageTotals {
    costMicroUsd: number
    inputTokens: number
    outputTokens: number
    cacheWriteTokens: number
    cacheReadTokens: number
    requests: number
}

export interface AiPurposeUsageSummary extends AiUsageTotals {
    purpose: AiInferencePurpose
}

export interface AiProviderUsageSummary extends AiUsageTotals {
    provider: AiProvider
}

export interface AiUserUsageSummary extends AiUsageTotals {
    customerId: string
    customerName: string
    installationId: string
    installationHost: string
    edgeUserId: string
    edgeUsername: string
}

export interface AiUsageSummary extends AiUsageTotals {
    resource: AiBillingResource.aiAssistant
    users: AiUserUsageSummary[]
    /** Total user groups before the highest-cost users list is capped. */
    userCount: number
    purposes: AiPurposeUsageSummary[]
    providers: AiProviderUsageSummary[]
}

export enum AiConversationStatus {
    pending = 'pending',
    running = 'running',
    blocked = 'blocked',
    completed = 'completed',
    failed = 'failed',
}

export enum ListAiConversationSortableField {
    title = 'title',
    customerName = 'customerName',
    installationHost = 'installationHost',
    edgeUsername = 'edgeUsername',
    updatedAt = 'updatedAt',
}

/** Rolling health of one inference provider, as the router sees it. */
export interface AiProviderHealth {
    provider: AiProvider
    /** Whether the adapter is in the registry; an unprovisioned provider carries no traffic. */
    registered: boolean
    circuitOpen: boolean
    openedAt: Date | null
    recentCalls: number
    recentFailures: number
    windowSeconds: number
}

export interface AiConversationFilter {
    customerId?: string
    searchName?: string
    /** Status of the conversation's most recent turn, so failures can be found without a query. */
    latestStatus?: AiConversationStatus
}

export interface AiConversationSummary {
    id: string
    threadId: string
    installationId: string
    installationHost: string
    customerId: string
    customerName: string
    edgeUserId: string
    edgeUsername: string
    title: string
    latestStatus: AiConversationStatus
    turnCount: number
    createdAt: Date
    updatedAt: Date
}

export interface AiConversationModelTrace {
    provider?: AiProvider
    system?: string
    messages: AiInferenceMessage[]
    tools: AiInferenceTool[]
    temperature?: number
    response?: AiInferenceResponse
}

export interface AiConversationTurn {
    jobId: string
    userMessageId: string
    status: AiConversationStatus
    scopeOutcome?: AiPolicyOutcome
    error?: string
    userMessage: {
        content: string
        images: AiImageAttachment[]
        createdAt: Date
    }
    assistantResponse?: AiInferenceResponse
    scopeTrace?: AiConversationModelTrace
    mainTrace?: AiConversationModelTrace
    createdAt: Date
    updatedAt: Date
    completedAt?: Date
}

export interface AiConversationDetail extends AiConversationSummary {
    turns: AiConversationTurn[]
}

export interface AiEulaAcceptanceRecord {
    edgeUserId: string
    edgeUsername: string
    version: string
    acceptedAt: Date
    /** Whether this acceptance covers the agreement version the assistant requires right now. */
    isCurrentVersion: boolean
}

export interface InstallationAiEulaAcceptances {
    /** Version the assistant requires; a user without an acceptance of it is blocked until they accept. */
    currentVersion: string
    /** Every recorded acceptance on the installation, so superseded versions stay visible. */
    acceptances: AiEulaAcceptanceRecord[]
}

export interface LoginRequest {
    username: string
    password: string
}

export interface LoginResult {
    state: 'authenticated'
    user: User
    passkeyRegistrationRequired: boolean
}

export type PasswordLoginResult = LoginResult | TotpLoginChallenge

export interface TotpLoginChallenge {
    state: 'totpRequired' | 'totpEnrollmentRequired'
    challengeId: string
    expiresAt: Date
    passkeyAvailable: boolean
}

export interface TotpCode {
    code: string
}

export interface CompleteTotpLogin extends TotpCode {
    challengeId: string
}

export interface BeginLoginTotpEnrollment {
    challengeId: string
}

export interface TotpEnrollment {
    enrollmentId: string
    secret: string
    uri: string
    expiresAt: Date
}

export interface CompleteTotpEnrollment extends TotpCode {
    enrollmentId: string
}

export interface MfaStatus {
    totpEnabled: boolean
    passkeyEnabled: boolean
}

export interface Passkey {
    id: string
    name: string
    createdAt: Date
    lastUsedAt?: Date
}

export type PasskeyAuthenticatorAttachment = 'cross-platform' | 'platform'
export type PasskeyAuthenticatorTransport = 'ble' | 'cable' | 'hybrid' | 'internal' | 'nfc' | 'smart-card' | 'usb'
export type PasskeyCredentialType = 'public-key'
export type PasskeyResidentKeyRequirement = 'discouraged' | 'preferred' | 'required'
export type PasskeyUserVerificationRequirement = 'discouraged' | 'preferred' | 'required'

export interface PasskeyAuthenticationExtensionsClientInputs {
    appid?: string
    credProps?: boolean
    hmacCreateSecret?: boolean
    minPinLength?: boolean
}

export interface PasskeyAuthenticationExtensionsClientOutputs {
    appid?: boolean
    credProps?: { rk?: boolean }
    hmacCreateSecret?: boolean
}

export interface PasskeyCredentialDescriptorJSON {
    id: string
    type: PasskeyCredentialType
    transports?: PasskeyAuthenticatorTransport[]
}

export interface PasskeyPublicKeyCredentialCreationOptionsJSON {
    rp: { id?: string; name: string }
    user: { id: string; name: string; displayName: string }
    challenge: string
    pubKeyCredParams: { alg: number; type: PasskeyCredentialType }[]
    timeout?: number
    excludeCredentials?: PasskeyCredentialDescriptorJSON[]
    authenticatorSelection?: {
        authenticatorAttachment?: PasskeyAuthenticatorAttachment
        requireResidentKey?: boolean
        residentKey?: PasskeyResidentKeyRequirement
        userVerification?: PasskeyUserVerificationRequirement
    }
    hints?: ('hybrid' | 'security-key' | 'client-device')[]
    attestation?: 'direct' | 'enterprise' | 'indirect' | 'none'
    attestationFormats?: ('fido-u2f' | 'packed' | 'android-safetynet' | 'android-key' | 'tpm' | 'apple' | 'none')[]
    extensions?: PasskeyAuthenticationExtensionsClientInputs
}

export interface PasskeyPublicKeyCredentialRequestOptionsJSON {
    challenge: string
    timeout?: number
    rpId?: string
    allowCredentials?: PasskeyCredentialDescriptorJSON[]
    userVerification?: PasskeyUserVerificationRequirement
    hints?: ('hybrid' | 'security-key' | 'client-device')[]
    extensions?: PasskeyAuthenticationExtensionsClientInputs
}

export interface PasskeyRegistrationResponseJSON {
    id: string
    rawId: string
    response: {
        clientDataJSON: string
        attestationObject: string
        authenticatorData?: string
        transports?: PasskeyAuthenticatorTransport[]
        publicKeyAlgorithm?: number
        publicKey?: string
    }
    authenticatorAttachment?: PasskeyAuthenticatorAttachment
    clientExtensionResults: PasskeyAuthenticationExtensionsClientOutputs
    type: PasskeyCredentialType
}

export interface PasskeyAuthenticationResponseJSON {
    id: string
    rawId: string
    response: {
        clientDataJSON: string
        authenticatorData: string
        signature: string
        userHandle?: string
    }
    authenticatorAttachment?: PasskeyAuthenticatorAttachment
    clientExtensionResults: PasskeyAuthenticationExtensionsClientOutputs
    type: PasskeyCredentialType
}

export interface PasskeyRegistrationOptions {
    ceremonyId: string
    options: PasskeyPublicKeyCredentialCreationOptionsJSON
}

export interface CompletePasskeyRegistration {
    ceremonyId: string
    name: string
    response: PasskeyRegistrationResponseJSON
}

export interface PasskeyAuthenticationOptions {
    ceremonyId: string
    options: PasskeyPublicKeyCredentialRequestOptionsJSON
}

export interface CompletePasskeyAuthentication {
    ceremonyId: string
    response: PasskeyAuthenticationResponseJSON
}

export interface RenamePasskey {
    name: string
}

export interface PasswordReauthentication {
    password: string
}

export type PasskeyReauthentication = PasswordReauthentication

export interface LogoutResponse {
    success: true
}

export interface BatchLicenseOperation {
    licenseIds: string[]
}

export interface BatchRevokeLicenses extends BatchLicenseOperation {}
export interface BatchReEnableLicenses extends BatchLicenseOperation {}
export interface BatchDeleteLicenses extends BatchLicenseOperation {}

export interface BatchResult<ID extends string = string> {
    succeeded: ID[]
    failed: ID[]
    // This is a hack to make the audit log populate the entity_name column for
    // batch operations with a comma-separated list of names
    names?: string[]
}

/**
 * @OAS_DESCRIPTION Parameters on which a list of users can be filtered.
 */
export interface UserFilter {
    userId?: string
    username?: string
}

export enum ListUserSortableField {
    userName = 'userName',
    role = 'role',
}

export interface User {
    id: string
    username: string
    role: Role
    managedBy: UserManagementSystem
    accountType: AccountType
    mfa: MfaStatus
}

export interface RegisteredSshPublicKey {
    fingerprint: string
    fileName: string
    createdAt: Date
    updatedAt: Date
}

export interface CurrentUserSshPublicKey {
    sshKey: RegisteredSshPublicKey | null
}

export interface SetSshPublicKey {
    publicKey: string
    fileName?: string
}

export interface SshCertificate {
    signedKey: string
    certificateSerial: string
    applianceSerial: string
    keyFingerprint: string
    validPrincipals: string[]
    validFromDate: Date
    validUntilDate: Date
    fileName: string
}

export enum UserManagementSystem {
    billingServer = 'billingServer',
    azure = 'azure',
}

export type NewUser = Pick<User, 'username' | 'role'> & {
    password: string
}
export type UserUpdate = Partial<NewUser>

export interface CustomerExternalUser {
    id: string
    username: string
    role: Role
    customer: Pick<Customer, 'id' | 'name'>
    mfa: MfaStatus
}

export interface ExternalUserCustomerAccess {
    customer: Pick<Customer, 'id' | 'name'>
    role: Role
}

export interface ExternalUser {
    id: string
    username: string
    customerAccess: ExternalUserCustomerAccess[]
    mfa: MfaStatus
}

export interface CurrentExternalUser extends CustomerExternalUser {
    customerAccess: ExternalUserCustomerAccess[]
}

export interface NewExternalUserCustomerAccess {
    customerId: string
    role: Role
}

export interface ExternalUserCustomerAccessUpdate {
    role: Role
}

/** Administrators never choose the password; the account is activated from an emailed link */
export interface NewExternalUser {
    username: string
    customerAccess: NewExternalUserCustomerAccess[]
}

export interface ChangePassword {
    currentPassword: string
    newPassword: string
}

export interface ConsolePasswordResetRequest {
    email: string
}

export interface CompleteConsolePasswordReset {
    token: string
    newPassword: string
}

export interface ConsoleInvitationRequest {
    email: string
    role: Role
}

export interface ConsoleInvitationTokenRequest {
    token: string
}

export interface ConsoleInvitationDetails {
    email: string
    organization: Pick<Customer, 'id' | 'name'>
    role: Role
    existingAccount: boolean
    expiresAt: Date
}

export interface ConsoleInvitation {
    id: string
    email: string
    role: Role
    expiresAt: Date
}

export interface StaffConsoleInvitation extends ConsoleInvitation {
    organization: Pick<Customer, 'id' | 'name'>
}

export type StaffExternalUserEntry =
    | { id: string; kind: 'user'; user: ExternalUser; pendingActivation?: Pick<ConsoleInvitation, 'id' | 'expiresAt'> }
    | { id: string; kind: 'invitation'; invitation: StaffConsoleInvitation }

export type ConsoleUserListEntry =
    | { kind: 'user'; user: CustomerExternalUser }
    | { kind: 'invitation'; invitation: ConsoleInvitation }

export interface AcceptNewConsoleInvitation extends ConsoleInvitationTokenRequest {
    password: string
}

export interface ConsoleInvitationAcceptance {
    organization: Pick<Customer, 'id' | 'name'>
    mfaChallenge?: TotpLoginChallenge
}

export interface Customer {
    id: string
    name: string
    deleted: boolean
    billingType: CustomerBillingType
    billingStartsAt: Date
    comment: string
    customerNumber: string
    /**
     * Billing periods starting at or after this instant consume Output RTUs per leg (a redundant
     * output costs 2); null keeps the original flat rule (one RTU covers a whole service). Set
     * per customer once the terms change is agreed, and immutable once a billing period has
     * rendered under it — rendered invoices are recomputed on every view and must never flip
     * rule retroactively.
     */
    legWeightedRtusStartAt: Date | null
    aiStatus: AiCustomerStatus
    aiBudgetMicroUsd: number | null
    /** Everything the customer has ever spent on AI, which trial credit is drawn against. */
    aiLifetimeSpendMicroUsd: number
    /** Spend in the current billing period, which the monthly budget is measured against. */
    aiPeriodSpendMicroUsd: number
}

// These are also stored in the billing_type table in the database
export enum CustomerBillingType {
    usageBased = 'Usage based',
    rtuBased = 'RTU based',
    other = 'Other',
    none = 'None',
}

export type NewCustomer = Pick<Customer, 'name' | 'billingStartsAt' | 'comment' | 'billingType'> & {
    customerNumber?: string
    /**
     * A new customer has no rendered history to protect, so any instant (including the past) is
     * accepted at creation — new agreements start on the leg-weighted model from day one. Absent
     * or null keeps the flat rule.
     */
    legWeightedRtusStartAt?: Date | null
    aiStatus?: AiCustomerStatus
    aiBudgetMicroUsd?: number | null
}
export type CustomerUpdate = Partial<NewCustomer> & {
    deleted?: boolean
    /** See Customer.legWeightedRtusStartAt. Must start after the customer's newest billing period; immutable once a period has rendered under it; null clears it while nothing has. */
    legWeightedRtusStartAt?: Date | null
}

export enum ListCustomerSortableField {
    id = 'id',
    name = 'name',
}

export interface NetSuiteCustomerImport {
    netsuiteId: string
    customerNumber: string
    accountUrl: string
}

export interface NetSuiteCustomerCandidate extends NetSuiteCustomerImport {
    name: string
}

interface NetSuiteCustomerSyncCounts {
    created: number
    linked: number
    updated: number
}

/** What one sync run found in NetSuite and what it changed in billing. Stored per run and shown to operators. */
export interface NetSuiteSyncResult {
    applied: boolean
    discovered: {
        appliances: number
        customers: number
        licenseSales: number
        licenseSalesWithSerial: number
    }
    imported: {
        allocationsCreated: number
        appliancesUpserted: number
        customers: NetSuiteCustomerSyncCounts
        licensePurchasesCreated: number
        licensePurchasesUpdated: number
        productsUpserted: number
    }
    warnings: string[]
}

/** One recorded import attempt. Failures carry `error` instead of a result, since nothing was applied. */
export interface NetSuiteSyncRun {
    startedAt: Date
    finishedAt: Date
    succeeded: boolean
    result?: NetSuiteSyncResult
    error?: string
}

/** Whether the scheduled import runs, and the outcome of recent attempts. */
export interface NetSuiteSyncStatus {
    syncEnabled: boolean
    /** Cadence from the deployment environment. Zero means this deployment never schedules a sync. */
    intervalMs: number
    /** The NetSuite account this database is pinned to, once a sync or import has run. */
    accountUrl?: string
    lastCompletedAt?: Date
    /** Newest first. Retained beyond the last run so warnings and failures stay reviewable. */
    recentRuns: NetSuiteSyncRun[]
}

export interface CustomerFilter {
    customerId?: string
    name?: string
    /** Case-insensitive partial match on customer name, customer number or purchase order number. */
    searchName?: string
    /** Defaults to true for lists; an explicit customer ID can still retrieve an archived customer. */
    excludeDeleted?: boolean
}

export enum LicenceInvalidationReason {
    // Revoked by admin
    revoked = 'revoked',
}

export const LICENSE_TYPES = {
    trial: {
        id: 'c794cc49-b0d1-46ed-afd3-e3622745d8ab',
        name: 'Trial',
    },
    usage: {
        id: '1b8752a6-805f-4175-a604-6329358f341d',
        name: 'Usage based',
    },
    rtu: {
        id: '1b410499-bc67-41fc-873c-0701f82d1604',
        name: 'RTU based',
    },
    volume: {
        id: '22b23119-0e9a-4b2a-8096-9ac885de0431',
        name: 'Volume package',
    },
} as const

export type LicenseType = (typeof LICENSE_TYPES)[keyof typeof LICENSE_TYPES]
export type LicenseId = LicenseType['id']

/**
 * @OAS_DESCRIPTION How to bill a customer / for which type of stream credits a customer is charged
 */
export enum CreditType {
    total = 'total', // all
    egress = 'egress',
    ingress = 'ingress',
}

// License is part of the API and will be visible to users
export interface License {
    id: string
    customer: Pick<Customer, 'id' | 'name'>
    purpose: string
    domain?: string

    product: Product['id']
    licenseType: LicenseType['id']
    creditType: CreditType

    licenseKey: string
    createdAt: Date
    /// When this license was last used
    activatedAt: Date | null
    invalidatedAt: Date | null
    invalidationReason: LicenceInvalidationReason | null

    entitlements?: LicenseEntitlements

    dockerUsername?: string
    dockerToken?: string
}

export type ConsoleLicense = Pick<
    License,
    | 'id'
    | 'purpose'
    | 'product'
    | 'licenseType'
    | 'creditType'
    | 'createdAt'
    | 'activatedAt'
    | 'invalidatedAt'
    | 'invalidationReason'
>

export type ConsoleLicenseKey = Pick<License, 'id' | 'licenseKey'>

export enum ConsoleLicenseSummaryKind {
    software = 'software',
    appliance = 'appliance',
    rtu = 'rtu',
}

/** A row in the external customer's combined license inventory. */
export interface ConsoleLicenseSummary {
    id: string
    kind: ConsoleLicenseSummaryKind
    name: string
    purchased: number | null
    allocated: number | null
    available: number | null
    createdAt: Date
    invalidatedAt: Date | null
}

export interface ConsoleLicenseSummaryFilter {
    searchName?: string
    kind?: ConsoleLicenseSummaryKind
}

export enum ListConsoleLicenseSummarySortableField {
    name = 'name',
    kind = 'kind',
    purchased = 'purchased',
    allocated = 'allocated',
    available = 'available',
    createdAt = 'created_at',
}

// LicenseDbo matches the database-object and should not be involved in the API
export interface LicenseDbo {
    id: string
    customer: Pick<Customer, 'id' | 'name'>
    purpose: string
    domain?: string

    product: Product['id']
    licenseType: LicenseType['id']
    creditType: License['creditType']

    licenseKey: string
    createdAt: Date
    /// When this license was last used
    activatedAt: Date | null
    invalidatedAt: Date | null
    invalidationReason: LicenceInvalidationReason | null

    entitlements?: LicenseEntitlements

    dockerUsername?: string
    dockerToken?: string
    dockerTokenUuid?: string
}

export type NewLicense = Required<Pick<License, 'purpose' | 'product' | 'licenseType' | 'creditType'>> & {
    customer: Customer['id']
    domain?: License['domain']
    entitlements?: License['entitlements']
}

export type UpdateLicense = {
    entitlements: LicenseEntitlements
}

// Used only for public route /api/license/:id
export interface PublicLicense {
    id: string
    domain?: string
    installations: LicenseInstallation[]
}
type LicenseInstallation = Pick<Installation, 'id' | 'host' | 'createdAt' | 'lastReportAt'>

export interface LicenseFilter {
    customerId?: string
    licenseKey?: string
    customerName?: string
    searchName?: string
    productId?: string
    licenseIds?: string[]
    excludeRevoked?: boolean
}

export enum ListLicenseSortableField {
    product = 'product',
    purpose = 'purpose',
    licenseType = 'license_type',
    createdAt = 'created_at',
    activatedAt = 'activated_at',
    invalidatedAt = 'invalidated_at',
    customerId = 'customer',
    customerName = 'name',
}

export interface Installation {
    id: string
    host: string
    releaseTag: string
    deploymentProvider: string
    license: Pick<License, 'id' | 'licenseKey' | 'purpose' | 'entitlements'>
    customer: Pick<Customer, 'id' | 'name'>
    product: Pick<Product, 'id' | 'name'>
    createdAt: Date
    updatedAt: Date
    lastReportAt: Date | null
    /** Start of last reported billing period for v1 (hourly periods, hour-granularity tracking) */
    lastReportedPeriodStartAt: Date | null
    /** Start of last reported billing period for v2 (monthly periods, minute-granularity tracking) */
    lastReportedPeriod2StartAt: Date | null
    deleted: boolean
}

/**
 * External customer view of an installation. Fields are picked explicitly
 * (never Omit) so new Installation fields stay internal until deliberately
 * exposed here and in toConsoleInstallation.
 */
export type ConsoleInstallation = Pick<
    Installation,
    | 'id'
    | 'host'
    | 'releaseTag'
    | 'deploymentProvider'
    | 'customer'
    | 'product'
    | 'createdAt'
    | 'updatedAt'
    | 'lastReportAt'
    | 'lastReportedPeriodStartAt'
    | 'lastReportedPeriod2StartAt'
    | 'deleted'
> & {
    license: Pick<License, 'id' | 'purpose' | 'entitlements'>
    /** Present when the installation list is filtered by RTU type. */
    rtuAllocation?: InstallationRtuAllocation
}

export enum ConsoleProductKind {
    edgeInstallation = 'edgeInstallation',
    appliance = 'appliance',
}

export interface ConsoleProduct {
    id: string
    kind: ConsoleProductKind
    name: string
    version: string | null
    lastReportAt: Date | null
    createdAt: Date
    deleted: boolean
    product: Pick<Product, 'id' | 'name'> | null
    applianceLicenseCount: number | null
    /** Hardware model imported from NetSuite, when known; null for installations and unimported serials. */
    applianceModel: ApplianceVariant | null
}

export interface ConsoleProductFilter {
    kind?: ConsoleProductKind
    searchName?: string
    name?: string
    product?: string
    version?: string
}

export enum ListConsoleProductSortableField {
    product = 'product',
    name = 'name',
    version = 'version',
    lastReported = 'last_reported',
}

export interface CreatedInstallation extends Installation {
    secret: string
    entitlementsToken: string // JWT of the entitlements
    dockerUsername?: string
    dockerAccessToken?: string
    // AGE secret key that decrypts the edge-os core airgap container bundle.
    // Present only for nimbraEdge installations.
    edgeOsCoreDecryptionKey?: string
}

export interface NewInstallation {
    host?: string
    licenseKey: string
    releaseTag: string
    deploymentProvider: string
    product?: Product['id'] // This should be mandatory, but is optional for backwards compatibility
    publicIps?: string[]
}

export interface UpdateInstallationLicenseRequest {
    secret: string
    host: string
    newLicenseKey: string
}

export interface UpdateInstallationLicenseResponse {
    secret: string
    entitlementsToken: string // JWT of the entitlements for the installation
}

export interface ValidateInstallationRequest {
    secret: string
    releaseTag: string
    deploymentProvider: string
}

export interface ValidateInstallationResponse {
    entitlementsToken: string // JWT of the entitlements for the installation
    docker?: {
        username: string
        accessToken: string
    }
}

export interface InstallationEntitlementsResponse {
    entitlementsToken: string // JWT of the entitlements for the installation}
}
export interface InstallationFilter {
    installationId?: string
    licenseId?: string
    host?: string
    customerId?: string
    searchName?: string
    licenseKey?: string
    releaseTag?: string
    deploymentProvider?: string
    excludeDeleted?: boolean
    hasAllocatedRtu?: boolean
    rtuTypeId?: string
}

export enum ListInstallationSortableField {
    id = 'id',
    host = 'host',
    customerName = 'customer_name',
    releaseTag = 'release_tag',
    deploymentProvider = 'deployment_provider',
    createdAt = 'created_at',
    lastReported = 'last_reported',
}

export interface BuildInfo {
    buildTime: Date
    release: string // Release version, i.e. R3.19.0
    commit: string // Git commit hash
    build: string // Pipeline id
    entitlementsPublicKey: string // Public key for verifying JWTs issued by this server
    entitlementsPublicKeyHash: string // First 8 characters of the base64-encoded sha256 hash of the public key
}

export interface AuditFilter {
    method?: string
    error?: boolean
    pathname?: string
    operation?: string
    username?: string
    entity?: string
    entityId?: string
    entityName?: string
    fromDate?: Date
    toDate?: Date
}

export interface BillingHour {
    start_at: Date
    ingress_records: Array<bigint> | null
    egress_records: Array<bigint> | null
    egress_credits: number | null
    ingress_credits: number | null
    stream_credits: number | null
    max_at: Date | null
    created_at: Date | null
}

export interface StreamRecord {
    id: bigint
    tags_id: bigint
    last_sample_at: Date
    start_at: Date
    total_bytes: bigint | null
    egress_credits: number | null
    ingress_credits: number | null
    duration_seconds: number | null
    stop_at: Date | null
}
export interface StreamRecordTags {
    id: bigint
    stream_id: number
    input_tags_id: bigint
    output_tags_id: bigint | null
    created_at: Date | null
}
export interface ApplianceTags {
    id: bigint
    appliance_id: string | null
    appliance_name: string | null
    appliance_type: string | null
    region_name: string | null
    created_at: Date | null
}
export interface InputTags {
    id: bigint
    group_tags_id: bigint
    appliance_tags_id: bigint
    secondary_appliance_tags_id: bigint | null
    channel_id: number
    input_id: string
    input_name: string
    input_port_mode: string | null
    input_port_count: number | null
    input_merge: boolean | null
    input_ingress_count: number | null
    created_at: Date | null
}
export interface OutputTags {
    id: bigint
    group_tags_id: bigint
    appliance_tags_id: bigint
    secondary_appliance_tags_id: bigint | null
    output_id: string | null
    output_name: string | null
    output_region: string | null
    output_port_mode: string | null
    output_port_count: number | null
    output_egress_count: number | null
    created_at: Date | null
}

export interface GroupTags {
    id: bigint
    group_id: string
    group_name: string
    created_at: Date | null
}

export type BillingHourlySlice = {
    billingHours: BillingHour[]
    streamRecords: StreamRecord[]
    streamRecordTags: StreamRecordTags[]
    applianceTags: ApplianceTags[]
    groupTags: GroupTags[]
    inputTags: InputTags[]
    outputTags: OutputTags[]
    // Older Edge releases report different metadata shapes; the billing server must accept them during upgrades.
    meta?: Record<string, unknown>
}

export type BillingPeriod = CommonBillingPeriod & {
    customer: Pick<Customer, 'id' | 'name' | 'customerNumber' | 'billingType'>
    license: Pick<License, 'id' | 'licenseKey' | 'licenseType' | 'creditType' | 'product'>
    host: Installation['host']
}

export type BillingReport = Pick<CommonBillingReport, 'startDate' | 'endDate' | 'summary'> & {
    periods: BillingPeriod[]
}

export interface Billing2EgressAttribution {
    egressSerial: string
    usageMinutes: number
    ratedMinutes: number
    outputLegs: number
    bytes: bigint
}

export interface Billing2ServiceDetail {
    serviceId: string
    usageMinutes: number
    ratedMinutes: number
    billableMinutes?: number
    bytes: bigint
    rtuCovered?: boolean
    /** Monthly portions attributed to each physical egress. Present on reported outputs. */
    egressAttributions?: Billing2EgressAttribution[]
    /** True when covered by an appliance flow license rather than a customer RTU. */
    flowCovered?: boolean
    /** Rated minutes covered when only part of a moved output is flow licensed. */
    flowCoveredRatedMinutes?: number
    /** RTUs consumed by this service under the leg-weighted rule (one per covered leg). */
    rtusConsumed?: number
    /** Rated minutes covered by RTUs under the leg-weighted rule; less than the remainder when the pool ran short. */
    rtuCoveredRatedMinutes?: number
}

export interface Billing2ServiceTypeReported {
    serviceType: string
    usageMinutes: number
    ratedMinutes: number
    bytes: bigint
    numServices: number
    services?: Billing2ServiceDetail[]
}

export interface Billing2ServiceTypeTotal extends Billing2ServiceTypeReported {
    billableMinutes: number
    rtuDeduction?: {
        allocatedRtus: number
        minutesCovered: number
        servicesFullyCovered: number
        /** RTUs consumed under the leg-weighted rule (a redundant output counts 2). Absent pre-gate. */
        rtusUsed?: number
        /** Rated minutes covered by RTUs under the leg-weighted rule. Absent pre-gate. */
        ratedMinutesCovered?: number
    }
    /** Appliance flow-license coverage applied to this service type (output only). */
    flowDeduction?: {
        flowLicensesUsed: number
        /** Rated minutes deducted, including partially covered moved outputs. */
        minutesCovered: number
        outputsFullyCovered: number
    }
}

export interface Billing2Period {
    periodId: number
    startAt: Date
    stopAt: Date
    reportedToDate: Date
    serviceTypes: Billing2ServiceTypeReported[]
}

export interface Billing2PeriodWithDetails extends Omit<Billing2Period, 'serviceTypes'> {
    id: string
    installationId: string
    host: string
    customer: Pick<Customer, 'id' | 'name' | 'legWeightedRtusStartAt'>
    license: Pick<License, 'id' | 'licenseKey' | 'licenseType'>
    totalUsageMinutes: number
    totalRatedMinutes: number
    totalBillableMinutes: number
    totalBytes: bigint
    serviceTypes: Billing2ServiceTypeTotal[]
}

export interface Billing2ReportedSlice {
    periods: Billing2Period[]
}

export interface Billing2Report extends ListResult<Billing2PeriodWithDetails> {}
export interface Billing2ReportWithSummary extends Billing2Report {
    summary: {
        totalBytes: bigint
        totalUsageMinutes?: number
        totalRatedMinutes?: number
        totalBillableMinutes?: number
        serviceTypes: {
            serviceType: string
            usageMinutes?: number
            ratedMinutes?: number
            billableMinutes?: number
            bytes: bigint
            numServices: number
            rtuDeduction?: {
                allocatedRtus: number
                minutesCovered: number
                servicesFullyCovered: number
                /** RTUs consumed under the leg-weighted rule, summed over the range. Absent pre-gate. */
                rtusUsed?: number
                /** Rated minutes covered by RTUs under the leg-weighted rule. Absent pre-gate. */
                ratedMinutesCovered?: number
            }
            flowDeduction?: {
                flowLicensesUsed: number
                minutesCovered: number
                outputsFullyCovered: number
            }
        }[]
    }
}

export interface RtuType {
    id: string
    name: string
}

/** The operator transfer that moved RTUs into or out of a customer's purchases. */
export interface CustomerRtuTransfer {
    direction: 'in' | 'out'
    /** The other customer in the transfer. */
    customer: { id: string; name: string }
}

/** An RTU transaction: a purchase at the quantity it credited, or one side of a transfer. */
export interface CustomerRtu {
    id: string
    customerId: string
    rtuType: RtuType
    /** Negative when a transfer moved the units out. */
    quantity: number
    reference: string
    createdBy: string
    createdAt: Date
    updatedAt: Date
    transfer?: CustomerRtuTransfer
    /**
     * Units this entry can still transfer to another customer: what remains of it, capped by the
     * customer's unallocated RTUs of its type. Absent on the outgoing side of a transfer.
     */
    transferable?: number
}

/** Moves unallocated units from one purchase to another customer's inventory. */
export interface LicenseTransferRequest {
    customerId: string
    quantity: number
}

/**
 * `encode_decode` covers every commercial channel product: the catalog has no encode-only or
 * decode-only SKU, so one appliance-wide channel count serves both directions. `edge_os`,
 * `hevc_422`, `asi`, and `standalone` are feature flags.
 */
export type ApplianceLicenseFamily = 'edge_os' | 'encode_decode' | 'hevc_422' | 'asi' | 'standalone'
export type ApplianceLicenseEntitlementMetric = 'codecCapacity' | 'featureFlag'
/**
 * Products add two families the signed bundle never carries: `flow` licenses are billing-only, and a
 * `bundle` is a NetSuite item group whose `components` are applied to one serial together.
 */
export type ApplianceLicenseProductFamily = ApplianceLicenseFamily | 'flow' | 'bundle'
export type ApplianceLicenseProductEntitlementMetric =
    | ApplianceLicenseEntitlementMetric
    | 'flows'
    | 'unlimitedFlows'
    | 'bundle'

/**
 * Identifies a sellable appliance license product (e.g. "NPM0102-0010"). The price-list catalog is
 * seeded by migration, but super users can define further products at runtime, so this stays a
 * free-form uppercase slug.
 */
export type ApplianceLicenseProductCode = string

/** One license a bundle product applies, `quantity` times, when the bundle is allocated to a serial. */
export interface ApplianceLicenseProductComponent {
    productCode: ApplianceLicenseProductCode
    quantity: number
}

export interface ApplianceLicenseProduct {
    code: ApplianceLicenseProductCode
    name: string
    licenseFamily: ApplianceLicenseProductFamily
    entitlementMetric: ApplianceLicenseProductEntitlementMetric
    entitlementValue: number
    codecCapacity?: ApplianceCodecCapacity
    compatibleModels?: ApplianceVariant[]
    /** The licenses a `bundle` product applies together; absent on every other product. */
    components?: ApplianceLicenseProductComponent[]
}

/** The entitlement metric is derived from the family and whether a flow product grants every flow. */
export type NewApplianceLicenseProduct = Omit<ApplianceLicenseProduct, 'entitlementMetric'> & {
    unlimited?: boolean
}

/** The NetSuite order a purchase came from. Absent when a staff member credited the account. */
export interface ApplianceLicensePurchaseOrder {
    salesOrderNumber: string
    /** The customer's own reference for the order (`PO #` in NetSuite), when the sales order has one. */
    purchaseOrderNumber?: string
}

/** The operator transfer that moved licenses into or out of a customer's inventory. */
export interface ApplianceLicensePurchaseTransfer {
    direction: 'in' | 'out'
    /** The appliance whose ownership transfer carried the licenses; absent for unallocated licenses. */
    serialNumber?: string
    /** The other customer in the transfer. */
    customer: { id: string; name: string }
}

/** A license transaction: a purchase at the quantity it credited, or one side of a transfer. */
export interface ApplianceLicensePurchase {
    id: string
    customerId: string
    productCode: ApplianceLicenseProductCode
    /** Negative when a transfer moved the units out. */
    quantity: number
    createdBy: string
    createdAt: Date
    netsuiteOrder?: ApplianceLicensePurchaseOrder
    transfer?: ApplianceLicensePurchaseTransfer
    /**
     * Unallocated units this entry can still transfer to another customer. Absent on the outgoing
     * side of a transfer.
     */
    transferable?: number
}

export interface NewApplianceLicensePurchase {
    customerId: string
    productCode: ApplianceLicenseProductCode
    quantity: number
}

export interface ApplianceLicensePurchaseFilter {
    customerId?: string
    productCode?: ApplianceLicenseProductCode
}

export interface ApplianceLicenseInventoryItem {
    customerId: string
    productCode: ApplianceLicenseProductCode
    purchased: number
    allocated: number
    available: number
}

export interface ApplianceLicenseInventoryFilter {
    customerId?: string
    productCode?: ApplianceLicenseProductCode
}

export interface ApplianceLicenseAllocation {
    id: string
    purchaseId: string
    customerId: string
    serialNumber: string
    productCode: ApplianceLicenseProductCode
    createdBy: string
    createdAt: Date
    /**
     * Unguessable key the appliance presents with its serial to fetch the software decryption key.
     * Issued only for standalone licenses.
     */
    activationKey?: string
}

export interface NewApplianceLicenseAllocation {
    customerId: string
    serialNumber: string
    productCode: ApplianceLicenseProductCode
}

export interface ApplianceLicenseAllocationFilter {
    customerId?: string
    serialNumber?: string
    productCode?: ApplianceLicenseProductCode
}

export interface ApplianceLicenseEntitlement {
    entitlementMetric: ApplianceLicenseEntitlementMetric
    entitlementValue: number
    codecPools?: ApplianceCodecCapacity[]
    compatibleModels?: ApplianceVariant[]
}

/** The per-family entitlements a bundle grants. At most one entry per family. */
export interface ApplianceLicenseBundleLicenses {
    edge_os?: ApplianceLicenseEntitlement
    encode_decode?: ApplianceLicenseEntitlement
    hevc_422?: ApplianceLicenseEntitlement
    asi?: ApplianceLicenseEntitlement
    /** Feature flag: the serial may run Edge OS standalone, i.e. unlock the software shipped in the image. */
    standalone?: ApplianceLicenseEntitlement
}

/**
 * The current signed appliance-enforced license bundle for a serial. Billing-only flow allocations
 * are deliberately excluded. Regenerated (with a bumped version) whenever an enforced allocation
 * changes; the highest version always wins wherever a serial is described more than once.
 */
export interface ApplianceLicenseBundle {
    serialNumber: string
    version: number
    issuedAt: Date
    token: string
    licenses: ApplianceLicenseBundleLicenses
}

export interface ApplianceLicenseBundleFilter {
    customerId?: string
    serialNumber?: string
}

/**
 * A single serial's signed bundle, trimmed to what an installation needs to install it: the serial it
 * is bound to, its version, and the token. The licenses are re-derived on the installation from the
 * verified token, so they are not carried here.
 */
export interface ApplianceLicenseBundleSyncItem {
    serialNumber: string
    version: number
    token: string
}

/**
 * Response of the installation-scoped bundle sync endpoint: the current signed bundle for every serial
 * the requesting installation's customer holds an active allocation for. The installation verifies each
 * token and reconciles it into its own store by version, so re-sending unchanged bundles is a no-op.
 */
export interface ApplianceLicenseBundleSyncResult {
    bundles: ApplianceLicenseBundleSyncItem[]
}

export interface Appliance {
    serialNumber: string
    productSku?: string
    purchasedAt?: Date
    customer: Pick<Customer, 'id' | 'name'>
}

export interface ApplianceFilter {
    customerId?: string
    serialNumber?: string
    searchName?: string
}

export interface ApplianceLicense {
    allocationId: string
    productCode: ApplianceLicenseProductCode
    productName: string
    licenseFamily: ApplianceLicenseProductFamily
    entitlementMetric: ApplianceLicenseProductEntitlementMetric
    entitlementValue: number
    codecCapacity?: ApplianceCodecCapacity
    compatibleModels?: ApplianceVariant[]
    /** The licenses a bundle allocation applied to the serial; absent for every other allocation. */
    components?: ApplianceLicenseProductComponent[]
    allocatedAt: Date
    /** See {@link ApplianceLicenseAllocation.activationKey}. */
    activationKey?: string
}

export interface ApplianceDetails extends Appliance {
    licenses: ApplianceLicense[]
}

export interface TransferApplianceOwnershipRequest {
    customerId: string
}

export enum ListApplianceSortableField {
    serialNumber = 'serial_number',
    customerName = 'customer_name',
}

/** Flow licenses are billing-only, so only appliance-enforced allocations return a signed bundle. */
export interface ApplianceLicenseAllocationResult {
    allocation: ApplianceLicenseAllocation
    bundle?: ApplianceLicenseBundle
}

/** Appliance license inventory exposed to the signed-in customer's portal. */
export type ConsoleApplianceLicenseInventoryItem = Omit<ApplianceLicenseInventoryItem, 'customerId'>

/** Customers know an order by their purchase order number; the sales order number stays internal. */
export type ConsoleApplianceLicensePurchaseOrder = Omit<ApplianceLicensePurchaseOrder, 'salesOrderNumber'>

/**
 * License transaction exposed without internal ownership, staff audit fields, the sales order number or
 * the other customer in a transfer.
 */
export type ConsoleApplianceLicensePurchase = Omit<
    ApplianceLicensePurchase,
    'customerId' | 'createdBy' | 'netsuiteOrder' | 'transfer' | 'transferable'
> & {
    /** Present whenever the purchase came from an order, even if that order has no purchase order number. */
    netsuiteOrder?: ConsoleApplianceLicensePurchaseOrder
    transfer?: Omit<ApplianceLicensePurchaseTransfer, 'customer'>
}

/** Appliance license allocation exposed without internal ownership and audit fields. */
export type ConsoleApplianceLicenseAllocation = Omit<ApplianceLicenseAllocation, 'customerId' | 'createdBy'>

/** Customer portal allocation input; the customer is always derived from the session. */
export type NewConsoleApplianceLicenseAllocation = Omit<NewApplianceLicenseAllocation, 'customerId'>

export interface ConsoleApplianceLicenseAllocationResult {
    allocation: ConsoleApplianceLicenseAllocation
    bundle?: ApplianceLicenseBundle
}

/** What a standalone appliance presents to prove its license when fetching the software decryption key. */
export interface ApplianceSoftwareKeyRequest {
    serialNumber: string
    /** The activation key issued with the serial's standalone license allocation. */
    activationKey: string
}

/** The AGE identity that decrypts the Edge Connect software bundle shipped in the appliance image. */
export interface ApplianceSoftwareKeyResponse {
    serialNumber: string
    decryptionKey: string
}

/** RTU transaction exposed without internal ownership, staff audit fields or the other customer in a transfer. */
export type ConsoleCustomerRtu = Omit<CustomerRtu, 'customerId' | 'createdBy' | 'transfer' | 'transferable'> & {
    transfer?: Omit<CustomerRtuTransfer, 'customer'>
}

export interface ConsoleRtuTotal {
    rtuType: RtuType
    quantity: number
    allocated: number
    available: number
}

export interface ConsoleCustomerRtuResult extends ListResult<ConsoleCustomerRtu> {
    totals: ConsoleRtuTotal[]
}

export interface NewCustomerRtu {
    customerId: string
    rtuTypeId: string
    quantity: number
    reference: string
}

export interface CustomerRtuFilter {
    customerId?: string
    rtuTypeId?: string
}

export interface InstallationRtu {
    installationId: string
    rtuType: RtuType
    quantity: number
    createdAt: Date
    updatedAt: Date
}

export interface InstallationRtuAllocation {
    rtuType: RtuType
    customerTotal: number
    totalAllocated: number
    thisInstallation: number
    available: number
}

export interface InstallationRtuHistory {
    id: string
    installationId: string
    rtuType: RtuType
    quantityChange: number
    operation: 'allocate' | 'deallocate'
    reference: string
    createdBy: string
    createdAt: Date
}

export interface UpdateInstallationRtu {
    rtuTypeId: string
    quantity: number
    reference?: string
}
